import type { NutritionEU } from "api-types";

const KJ_PER_KCAL = 4.184;

/** Unrounded. A value missing from the declaration, or not derivable, is null. */
export type NutritionEUColumn = {
  energyKj: number | null;
  energyKcal: number | null;
  fat: number | null;
  saturates: number | null;
  monounsaturates: number | null;
  polyunsaturates: number | null;
  carbohydrate: number | null;
  sugars: number | null;
  polyols: number | null;
  starch: number | null;
  fibre: number | null;
  protein: number | null;
  salt: number | null;
};

export type NutritionEUColumns = {
  perPortion: NutritionEUColumn;
  /** Per 100 of the declaration's unit. */
  per100: NutritionEUColumn;
};

const column = (
  nutrition: NutritionEU,
  factor: number | null
): NutritionEUColumn => {
  const scale = (value: number | null) =>
    value === null || factor === null ? null : value * factor;
  const energyKcal = scale(nutrition.energyKcal);
  return {
    energyKj: energyKcal === null ? null : energyKcal * KJ_PER_KCAL,
    energyKcal,
    fat: scale(nutrition.fat),
    saturates: scale(nutrition.saturates),
    monounsaturates: scale(nutrition.monounsaturates),
    polyunsaturates: scale(nutrition.polyunsaturates),
    carbohydrate: scale(nutrition.carbohydrate),
    sugars: scale(nutrition.sugars),
    polyols: scale(nutrition.polyols),
    starch: scale(nutrition.starch),
    fibre: scale(nutrition.fibre),
    protein: scale(nutrition.protein),
    salt: scale(nutrition.salt),
  };
};

/**
 * Both columns of an EU nutrition table. The column `valuesPer` names holds
 * the stored values; the other is scaled through the portion size.
 */
export const nutritionEUColumns = (
  nutrition: NutritionEU
): NutritionEUColumns => {
  const { portionSize, valuesPer } = nutrition;
  const toPortion =
    valuesPer === "portion"
      ? 1
      : valuesPer === "100" && portionSize !== null
      ? portionSize / 100
      : null;
  const to100 =
    valuesPer === "100"
      ? 1
      : valuesPer === "portion" && portionSize !== null
      ? 100 / portionSize
      : null;
  return {
    perPortion: column(nutrition, toPortion),
    per100: column(nutrition, to100),
  };
};
